import React, { useRef, useState } from 'react';
import { Download, Copy, Check, MapPin, Plus, Trash2, Image as ImageIcon } from 'lucide-react';
import * as htmlToImage from 'html-to-image';

/**
 * TEMPLATE 3 — "Where We Are Now"
 * Reusable daily-post template for national scale reporting.
 *   - Faded NZ map background
 *   - Kaitaia → Bluff route line + pins for each active region
 *   - Headline: 10 WEEKS. 250 FARMS. ONE FLOW.
 *   - Configurable "Currently working through:" region list (edit → download fresh PNG)
 *
 * Formats: Portrait 1080×1350 · Landscape 1200×630
 */

// Every region we might tick — coordinates in the NZ map's internal 500×800 viewBox.
// Roughly geographic (north to south).
const NZ_REGIONS = [
  { name: 'Northland',        x: 235, y: 90 },
  { name: 'Auckland',         x: 250, y: 145 },
  { name: 'Waikato',          x: 275, y: 195 },
  { name: 'Bay of Plenty',    x: 320, y: 205 },
  { name: 'Gisborne',         x: 360, y: 235 },
  { name: 'Taranaki',         x: 235, y: 245 },
  { name: 'Hawke\'s Bay',     x: 335, y: 265 },
  { name: 'Manawatu',         x: 290, y: 285 },
  { name: 'Wellington',       x: 290, y: 320 },
  { name: 'Nelson/Tasman',    x: 245, y: 355 },
  { name: 'Marlborough',      x: 285, y: 355 },
  { name: 'West Coast',       x: 200, y: 435 },
  { name: 'Canterbury',       x: 260, y: 475 },
  { name: 'Otago',            x: 220, y: 570 },
  { name: 'Southland',        x: 175, y: 640 },
];

// Kaitaia (top) & Bluff (bottom) — endpoints for the route line
const KAITAIA = { x: 215, y: 55 };
const BLUFF   = { x: 175, y: 680 };

// Stylised NZ silhouette (two islands). Simplified but recognisable.
const NZ_PATH_NORTH = "M255,50 Q210,60 195,95 Q175,130 200,155 Q210,180 245,175 Q275,180 285,205 Q310,210 335,230 Q360,240 355,260 Q345,285 320,285 Q295,290 280,275 Q260,280 245,300 Q225,315 235,330 Q255,340 280,335 Q305,340 300,320 Q315,305 300,285 Q285,275 275,250 Q255,240 235,255 Q220,240 240,220 Q225,205 220,180 Q235,160 260,165 Q285,155 275,130 Q265,105 240,95 Q225,80 255,50 Z";
const NZ_PATH_SOUTH = "M200,370 Q170,395 165,435 Q155,475 175,510 Q185,555 175,600 Q170,650 175,680 Q195,690 205,660 Q220,625 210,585 Q225,545 245,510 Q265,475 255,445 Q275,420 280,390 Q270,360 240,365 Q220,365 200,370 Z";

const WhereWeAreNowAd = () => {
  const portraitRef = useRef(null);
  const landscapeRef = useRef(null);
  const [downloading, setDownloading] = useState(null);
  const [copied, setCopied] = useState(false);

  // Editable state — persists across sessions in localStorage
  const [headline, setHeadline] = useState(() =>
    localStorage.getItem('wwan-headline') || '10 WEEKS. 250 FARMS. ONE FLOW.'
  );
  const [activeRegions, setActiveRegions] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('wwan-regions') || 'null');
      return saved || ['Waikato', 'Taranaki', 'Manawatu'];
    } catch { return ['Waikato', 'Taranaki', 'Manawatu']; }
  });

  const updateRegions = (next) => {
    setActiveRegions(next);
    localStorage.setItem('wwan-regions', JSON.stringify(next));
  };
  const updateHeadline = (val) => {
    setHeadline(val);
    localStorage.setItem('wwan-headline', val);
  };

  const toggleRegion = (name) => {
    if (activeRegions.includes(name)) {
      updateRegions(activeRegions.filter((r) => r !== name));
    } else {
      updateRegions([...activeRegions, name]);
    }
  };

  const activePoints = activeRegions
    .map((n) => NZ_REGIONS.find((r) => r.name === n))
    .filter(Boolean);

  const handleDownload = async (ref, filename) => {
    if (!ref.current) return;
    setDownloading(filename);
    try {
      const dataUrl = await htmlToImage.toPng(ref.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#0a1f13',
      });
      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
      alert('Could not generate the PNG. Try a different browser if it persists.');
    } finally {
      setDownloading(null);
    }
  };

  // ────────────────────────────────────────
  // Ad Caption for copy-paste
  // ────────────────────────────────────────
  const regionsBulletList = activeRegions.map((r) => `📍  ${r}`).join('\n');
  const adCopy = `WHERE WE ARE NOW 📍

${headline}

The Measured Livestock team is on the ground right across NZ — Kaitaia to Bluff — weighing dairy heifers alongside TOP inspection programs.

Currently working through:
${regionsBulletList}

Every animal captured live in the yards. Every job Animal ID Verified.

Follow the journey.
👉 measuredlivestock.com

#NZDairy #HeiferWeighing #DairyNZ #NZFarming #MeasuredLivestock #AnimalIDVerified`;

  const handleCopy = () => {
    navigator.clipboard.writeText(adCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ────────────────────────────────────────
  // NZ Map SVG (shared by both formats)
  // ────────────────────────────────────────
  const NZMap = ({ opacity = 0.16, showRoute = true, showActivePins = true, showEndpoints = true }) => (
    <svg
      viewBox="0 0 500 800"
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', display: 'block' }}
    >
      {/* Faded map silhouette */}
      <g style={{ opacity }}>
        <path d={NZ_PATH_NORTH} fill="#ff8533" stroke="#ff8533" strokeWidth="2" />
        <path d={NZ_PATH_SOUTH} fill="#ff8533" stroke="#ff8533" strokeWidth="2" />
      </g>

      {/* Route line Kaitaia → Bluff (curved, dashed) */}
      {showRoute && (
        <g>
          <path
            d={`M${KAITAIA.x},${KAITAIA.y} C 320,200 320,500 ${BLUFF.x},${BLUFF.y}`}
            fill="none"
            stroke="#ff8533"
            strokeWidth="3"
            strokeDasharray="8 8"
            opacity="0.65"
          />
        </g>
      )}

      {/* Kaitaia + Bluff endpoints */}
      {showEndpoints && (
        <>
          <g transform={`translate(${KAITAIA.x},${KAITAIA.y})`}>
            <circle r="10" fill="#ff6b00" />
            <circle r="10" fill="none" stroke="#ff6b00" strokeWidth="2" opacity="0.35" style={{ transform: 'scale(1.8)' }} />
            <text x="18" y="6" fill="#fff" fontSize="16" fontWeight="700" letterSpacing="2">KAITAIA</text>
          </g>
          <g transform={`translate(${BLUFF.x},${BLUFF.y})`}>
            <circle r="10" fill="#ff6b00" />
            <circle r="10" fill="none" stroke="#ff6b00" strokeWidth="2" opacity="0.35" style={{ transform: 'scale(1.8)' }} />
            <text x="18" y="6" fill="#fff" fontSize="16" fontWeight="700" letterSpacing="2">BLUFF</text>
          </g>
        </>
      )}

      {/* Active region pins — larger, glow */}
      {showActivePins && activePoints.map((p) => (
        <g key={p.name} transform={`translate(${p.x},${p.y})`}>
          <circle r="18" fill="#ff8533" opacity="0.18" />
          <circle r="11" fill="#ff8533" opacity="0.35" />
          <circle r="6" fill="#ff6b00" stroke="#fff" strokeWidth="2" />
          <text x="14" y="5" fill="#fff" fontSize="15" fontWeight="800" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.6)' }}>
            {p.name}
          </text>
        </g>
      ))}
    </svg>
  );

  // ────────────────────────────────────────
  return (
    <section
      className="bg-gradient-to-br from-[#0a1f13] via-[#0f2818] to-[#0a1f13] py-12"
      data-testid="where-we-are-now-section"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <span className="inline-block bg-[#ff6b00] text-white px-4 py-1 rounded-full text-xs uppercase tracking-widest font-bold mb-3">
            Template 3 · Where We Are Now · Daily Post
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-2 flex items-center justify-center gap-3">
            <MapPin className="w-9 h-9 text-[#ff6b00]" />
            Where We Are Now
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto">
            Reusable daily template — update the headline &amp; active regions below, then download a fresh PNG.
          </p>
        </div>

        {/* ═══════════ EDITOR ═══════════ */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-5 mb-8 backdrop-blur">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold text-[#ff8533] tracking-widest uppercase mb-2">
                Headline
              </label>
              <input
                type="text"
                value={headline}
                onChange={(e) => updateHeadline(e.target.value)}
                data-testid="wwan-headline-input"
                className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-white text-lg font-bold focus:outline-none focus:border-[#ff8533]"
                placeholder="10 WEEKS. 250 FARMS. ONE FLOW."
              />
              <p className="text-white/50 text-xs mt-2">
                Keep it short. Three-beat headlines work best (e.g. <em>"WEEK 3. 68 FARMS. 7,800 HEIFERS."</em>)
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#ff8533] tracking-widest uppercase mb-2">
                Currently working through <span className="text-white/50 font-normal normal-case tracking-normal">— tap to toggle</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {NZ_REGIONS.map((r) => {
                  const on = activeRegions.includes(r.name);
                  return (
                    <button
                      key={r.name}
                      onClick={() => toggleRegion(r.name)}
                      data-testid={`wwan-region-toggle-${r.name.replace(/[^a-zA-Z]/g, '')}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                        on
                          ? 'bg-[#ff6b00] text-white border-[#ff6b00] shadow-lg shadow-[#ff6b00]/30'
                          : 'bg-transparent text-white/70 border-white/25 hover:border-white/60'
                      }`}
                    >
                      {on ? '📍 ' : ''}{r.name}
                    </button>
                  );
                })}
              </div>
              <p className="text-white/50 text-xs mt-3">
                {activeRegions.length} region{activeRegions.length === 1 ? '' : 's'} active · pins &amp; caption update automatically.
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 items-start mb-8">
          {/* ═══════════ PORTRAIT 1080 × 1350 ═══════════ */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> FB Feed Portrait · 1080 × 1350
              </span>
              <button
                onClick={() => handleDownload(portraitRef, 'measuredlivestock-where-we-are-now-1080x1350.png')}
                disabled={downloading === 'measuredlivestock-where-we-are-now-1080x1350.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-wwan-portrait-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-where-we-are-now-1080x1350.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.42)',
                  width: 1080,
                  height: 1350,
                  marginRight: -1080 * 0.58,
                  marginBottom: -1350 * 0.58,
                }}
              >
                <div
                  ref={portraitRef}
                  style={{
                    width: 1080,
                    height: 1350,
                    position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    background: 'radial-gradient(circle at 30% 20%, #163826 0%, #0a1f13 55%, #06110a 100%)',
                    color: 'white',
                  }}
                >
                  {/* Grain / subtle noise */}
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'radial-gradient(circle at 80% 90%, rgba(255,107,0,0.10), transparent 45%)',
                    pointerEvents: 'none',
                  }} />

                  {/* Faded NZ Map full-bleed */}
                  <div style={{ position: 'absolute', top: 210, left: 0, right: 0, bottom: 340 }}>
                    <NZMap opacity={0.18} />
                  </div>

                  {/* Brand + duration */}
                  <div style={{
                    position: 'absolute', top: 40, left: 56, right: 56,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 5, height: 26, background: '#ff6b00' }} />
                      <span style={{ fontSize: 13, letterSpacing: 5, fontWeight: 700, opacity: 0.92, textTransform: 'uppercase' }}>
                        Measured Livestock
                      </span>
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '8px 18px', borderRadius: 999,
                      fontSize: 11, fontWeight: 800, letterSpacing: 3, textTransform: 'uppercase',
                    }}>
                      📍 Where We Are Now
                    </div>
                  </div>

                  {/* Headline */}
                  <div style={{ position: 'absolute', top: 108, left: 56, right: 56, textAlign: 'center' }}>
                    <h1 style={{
                      fontSize: 56, lineHeight: 1.05, fontWeight: 900, margin: 0,
                      letterSpacing: -1.5,
                      textShadow: '0 4px 22px rgba(0,0,0,0.6)',
                    }}>
                      {headline}
                    </h1>
                  </div>

                  {/* "Currently working through" panel — pinned to bottom */}
                  <div style={{
                    position: 'absolute', left: 56, right: 56, bottom: 170,
                    background: 'rgba(0,0,0,0.6)',
                    backdropFilter: 'blur(8px)',
                    border: '1.5px solid rgba(255,107,0,0.5)',
                    borderRadius: 16, padding: '22px 26px',
                  }}>
                    <div style={{
                      fontSize: 12, fontWeight: 800, color: '#ff8533',
                      letterSpacing: 4, textTransform: 'uppercase', marginBottom: 14,
                    }}>
                      Currently working through
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {activeRegions.length === 0 ? (
                        <div style={{ fontSize: 18, opacity: 0.6 }}>— Add a region above —</div>
                      ) : activeRegions.map((r) => (
                        <div key={r} style={{
                          background: 'rgba(255,107,0,0.15)',
                          border: '1px solid rgba(255,107,0,0.55)',
                          borderRadius: 999,
                          padding: '10px 18px',
                          fontSize: 20, fontWeight: 700,
                          display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                          <span style={{ fontSize: 18 }}>📍</span>{r}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <div style={{ position: 'absolute', bottom: 60, left: 56, right: 56, textAlign: 'center' }}>
                    <div style={{
                      fontSize: 12, fontWeight: 700, color: '#ff8533',
                      letterSpacing: 3, textTransform: 'uppercase', marginBottom: 8,
                    }}>
                      Follow the journey
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: 0.5 }}>
                      measuredlivestock.com
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ═══════════ LANDSCAPE 1200 × 630 ═══════════ */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> FB Feed Landscape · 1200 × 630
              </span>
              <button
                onClick={() => handleDownload(landscapeRef, 'measuredlivestock-where-we-are-now-1200x630.png')}
                disabled={downloading === 'measuredlivestock-where-we-are-now-1200x630.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-wwan-landscape-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-where-we-are-now-1200x630.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.55)',
                  width: 1200,
                  height: 630,
                  marginRight: -1200 * 0.45,
                  marginBottom: -630 * 0.45,
                }}
              >
                <div
                  ref={landscapeRef}
                  style={{
                    width: 1200,
                    height: 630,
                    position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    background: 'radial-gradient(circle at 25% 30%, #163826 0%, #0a1f13 55%, #06110a 100%)',
                    color: 'white',
                    display: 'flex',
                  }}
                >
                  {/* Left column: NZ map */}
                  <div style={{
                    width: 380, height: '100%', position: 'relative',
                    flexShrink: 0,
                    borderRight: '1px solid rgba(255,255,255,0.06)',
                  }}>
                    <div style={{ position: 'absolute', inset: '20px 10px' }}>
                      <NZMap opacity={0.22} />
                    </div>
                  </div>

                  {/* Right column: content */}
                  <div style={{
                    flex: 1, padding: '36px 44px',
                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                    position: 'relative',
                  }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 5, height: 24, background: '#ff6b00' }} />
                        <span style={{ fontSize: 12, letterSpacing: 4, fontWeight: 700, textTransform: 'uppercase', opacity: 0.92 }}>
                          Measured Livestock
                        </span>
                      </div>
                      <div style={{
                        background: '#ff6b00', color: 'white', padding: '6px 14px', borderRadius: 999,
                        fontSize: 10, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase',
                      }}>
                        📍 Where We Are Now
                      </div>
                    </div>

                    <div>
                      <h1 style={{
                        fontSize: 46, lineHeight: 1.02, fontWeight: 900, margin: '4px 0 20px',
                        letterSpacing: -1, textShadow: '0 4px 18px rgba(0,0,0,0.55)',
                      }}>
                        {headline}
                      </h1>

                      <div style={{
                        fontSize: 11, fontWeight: 800, color: '#ff8533',
                        letterSpacing: 3, textTransform: 'uppercase', marginBottom: 10,
                      }}>
                        Currently working through
                      </div>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {activeRegions.length === 0 ? (
                          <div style={{ fontSize: 14, opacity: 0.6 }}>— Add a region above —</div>
                        ) : activeRegions.map((r) => (
                          <div key={r} style={{
                            background: 'rgba(255,107,0,0.15)',
                            border: '1px solid rgba(255,107,0,0.55)',
                            borderRadius: 999,
                            padding: '7px 14px',
                            fontSize: 15, fontWeight: 700,
                            display: 'flex', alignItems: 'center', gap: 6,
                          }}>
                            <span style={{ fontSize: 13 }}>📍</span>{r}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
                      borderTop: '1px solid rgba(255,255,255,0.10)', paddingTop: 12, marginTop: 12,
                    }}>
                      <div>
                        <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.75, marginBottom: 2 }}>
                          Follow the journey
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 800 }}>measuredlivestock.com</div>
                      </div>
                      <div style={{
                        background: '#ff6b00', color: 'white', padding: '10px 22px', borderRadius: 8,
                        fontSize: 14, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                        boxShadow: '0 8px 22px rgba(0,0,0,0.4)',
                      }}>
                        Follow Along →
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Caption */}
        <div className="bg-white rounded-xl p-6 shadow-2xl">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-bold text-[#1a4d2e] flex items-center gap-2 text-lg">
              <MapPin className="w-5 h-5 text-[#ff6b00]" />
              Ready-to-Post Caption
            </h3>
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 bg-[#1a4d2e] hover:bg-[#1a4d2e]/90 text-white px-4 py-2 rounded-lg text-sm font-semibold"
              data-testid="copy-wwan-ad-copy-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Caption'}
            </button>
          </div>
          <pre className="whitespace-pre-wrap text-sm text-[#4a4a4a] bg-[#f7f5f1] border border-[#e5ddd5] p-4 rounded-lg leading-relaxed font-sans">
{adCopy}
          </pre>
          <p className="text-xs text-[#8d6e63] mt-3">
            💡 <strong>Daily workflow:</strong> Update headline &amp; toggle the regions you're in this week
            → click <strong>Download PNG</strong> (portrait for feed, landscape for link previews) →
            copy caption → post. Your last selection is saved locally.
          </p>
        </div>
      </div>
    </section>
  );
};

export default WhereWeAreNowAd;
